import * as React from 'react';
import { Subtract, TransitionType } from '../../utils/types';
import { LoaderEnhancerProperties, withLoader } from './LoaderEnhancer';
import { authenticationService } from '../../services/authentication';
import { errorHandler } from '../../services/errorHandler';
import { AccountSettings, Attributes } from 'fs-api-client';
import { createDefaultApi } from '../../utils/createApi';
import { ClientResponse } from 'http';
import { productType } from '../../utils/constants';
import TransitionTypeEnum = AccountSettings.TransitionTypeEnum;
import { unawaited } from '../../utils/cognito';

export interface ProfileInjectorProperties extends LoaderEnhancerProperties {
    transitionType: TransitionType;
    attributes: Attributes;
    fetchProfile: (showLoader: boolean) => {};
    setOnProfileFetched: (callback: () => void) => void;
    email: string;
    userId: string;
}

interface ProfileInjectorState {
    attributes: Attributes | null;
    loading: boolean;
    isUserSuspended: boolean;
    securityScopes?: string [];
    email: string;
    transitionType: TransitionType;
    userId: string;
}

interface GetUserResponse {
    response: ClientResponse;
    body: AccountSettings;
}

interface ProfileInjectorFetcher {
    updateIsUserSuspended: (result: GetUserResponse) => void;
}

async function getAccountSettings(): Promise<GetUserResponse> {
    const api = await createDefaultApi();
    return await api.getAccountSettings();
}

const injectors: ProfileInjectorFetcher[] = [];
const ONE_MINUTE = 60000;

export async function fetchAndUpdateAccountSetting() {
    if (injectors.length < 1) {
        return;
    }
    const result = await getAccountSettings();
    for (const injector of injectors) {
        injector.updateIsUserSuspended(result);
    }
}

setInterval(fetchAndUpdateAccountSetting, ONE_MINUTE);

type OnProfileFetchedCallback = (() => void) | null;

let cachedAttributes: any = null;

export function injectProfile<P extends ProfileInjectorProperties>(component: React.ComponentType<P>) {
    const loaderName = 'profileInjector';
    let DynamicComponent: any = withLoader(component, loaderName);

    return class ProfileInjector extends React.Component<Subtract<P, ProfileInjectorProperties>, ProfileInjectorState> implements ProfileInjectorFetcher {
        private onProfileFetchedCallback: OnProfileFetchedCallback = null;
        private isCompMounted: boolean = false;

        constructor(props: P) {
            super(props);
            this.fetchProfile = this.fetchProfile.bind(this);
            this.setOnProfileFetched = this.setOnProfileFetched.bind(this);

            this.fetchDataAndSetState = this.fetchDataAndSetState.bind(this);

            this.state = {
                attributes: cachedAttributes,
                loading: true,
                isUserSuspended: false,
                email: '',
                transitionType: TransitionTypeEnum.None,
                userId: ''
            };
        }

        componentDidMount() {
            this.isCompMounted = true;
            injectors.push(this);
            unawaited(this.fetchProfile(true));
        }

        componentWillUnmount() {
            this.isCompMounted = false;
            const injectorIndex = injectors.indexOf(this);
            injectors.splice(injectorIndex, 1);
        }

        updateIsUserSuspended(user: GetUserResponse) {
            if (this.isCompMounted) {
                this.setState({isUserSuspended: user.body.isSuspended});
            }
        }

        render() {
            return (
                <DynamicComponent
                    {...this.props}
                    transitionType={this.state.transitionType}
                    isUserSuspended={this.state.isUserSuspended}
                    securityScopes={this.state.securityScopes}
                    loading={this.state.loading}
                    setOnProfileFetched={this.setOnProfileFetched}
                    fetchProfile={this.fetchProfile}
                    attributes={this.state.attributes}
                    email={this.state.email}
                    userId={this.state.userId}
                />
            );
        }

        async fetchProfile(showLoader: boolean = false): Promise<void> {
            if (!this.isCompMounted) { return; }

            this.setState(
                { loading: showLoader },
                this.fetchDataAndSetState
            );
        }

        private async fetchDataAndSetState() {
            try {
                // TODO Parallelize requests
                const user = await getAccountSettings();
                const attributes = await authenticationService.getUserAttributes();
                const email = await authenticationService.getCurrentUserEmail();

                ProfileInjector.verifyAttributesNotNull(attributes);

                cachedAttributes = attributes;

                if (this.isBackofficeAndUserHasNoSecurityScopes(user)) {
                    await authenticationService.signOut();
                    return;
                }

                this.setState(
                    {
                        attributes: attributes,
                        loading: false,
                        isUserSuspended: user.body.isSuspended,
                        securityScopes: user.body.securityScopes,
                        transitionType: user.body.transitionType,
                        userId: user.body.userId,
                        email: email
                    },
                    () => {
                        if (this.onProfileFetchedCallback) {
                            this.onProfileFetchedCallback();
                        }
                    }
                );
            } catch (error) {
                errorHandler(error);
            }
        }

        private isBackofficeAndUserHasNoSecurityScopes(user: GetUserResponse) {
            return productType === 'backoffice' &&
                user.body.securityScopes &&
                user.body.securityScopes.length === 0;
        }

        private static verifyAttributesNotNull(attributes: Attributes): void {
            if (!attributes) {
                throw new Error('Attributes are null.');
            }
        }

        private setOnProfileFetched(onProfileFetchedCallback: OnProfileFetchedCallback): void {
            this.onProfileFetchedCallback = onProfileFetchedCallback;
        }
    };
}



// WEBPACK FOOTER //
// ./src/webportal/components/hocs/ProfileInjector.tsx